/**
 * One module behind a door: fetched at the browser's first idle moment once it
 * may be used, or at once when it is wanted (2026-09-24).
 *
 * For a surface people reach with a click (a panel, a picker) whose code should
 * not ride the file every visitor downloads to see the chat bar, but which has to
 * open the moment it is pressed. `useDeferredModule` fetches on every mount and
 * knows nothing about "may be used" or "wanted now"; this does:
 *  - nothing is fetched until `enabled`, so a signed-out visitor never pays;
 *  - then the download starts at the first idle moment, so by the time a person
 *    presses the control the module is almost always already here;
 *  - `wanted` (pressed before it arrived) starts the download at once;
 *  - one download per page, shared by every mount. A failed one is forgotten, so
 *    the next press tries again; retryImport retries a transient failure and hands
 *    a chunk that is gone for good to the stale-tab self-heal.
 * A failure is a state, never a throw: the surface simply does not open.
 *
 * NOT React.lazy (src/utils/useDeferredModule.ts has the history). The dynamic
 * import() stays in the caller's own door file, where the first-paint gate
 * (scripts/check-chat-first-paint.mjs, R4) finds it with its chunk name.
 */
import { useEffect, useState } from 'react';
import { retryImport } from './lazyWithRetry';

const LOAD_RETRIES = 3;
const LOAD_RETRY_DELAY_MS = 1000;
/** The longest an idle start may wait on a busy main thread. */
const IDLE_TIMEOUT_MS = 2000;

export interface OnDemandModule<M> {
  /** The module once it has arrived, or null. Starts the download when `enabled`. */
  useModule: (enabled: boolean, wanted?: boolean) => M | null;
  /**
   * The module, fetched now if it has not arrived — for code that needs it at a
   * press rather than to draw it (a save from the library). The same single
   * download `useModule` shares; a failed one is forgotten, so the next call
   * tries again.
   */
  load: () => Promise<M>;
  /** Tests only: forget the download. */
  resetForTests: () => void;
}

export function createOnDemandModule<M>(loader: () => Promise<M>): OnDemandModule<M> {
  let loaded: M | null = null;
  let loading: Promise<M> | null = null;

  const start = (): Promise<M> => {
    if (!loading) {
      loading = retryImport(loader, LOAD_RETRIES, LOAD_RETRY_DELAY_MS).then(
        (mod) => {
          loaded = mod;
          return mod;
        },
        (err) => {
          loading = null;
          throw err;
        },
      );
    }
    return loading;
  };

  function useModule(enabled: boolean, wanted = false): M | null {
    const [mod, setMod] = useState<M | null>(loaded);

    useEffect(() => {
      if (!enabled || mod) return undefined;
      if (loaded) {
        setMod(loaded);
        return undefined;
      }
      let alive = true;
      const go = () => {
        start().then(
          (m) => {
            if (alive) setMod(m);
          },
          () => {
            /* absent, never thrown: the next press tries again */
          },
        );
      };
      if (wanted) {
        go();
        return () => {
          alive = false;
        };
      }
      const w = window as unknown as {
        requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
        cancelIdleCallback?: (h: number) => void;
      };
      // An idle handle is never handed to clearTimeout: the numbers can collide.
      if (typeof w.requestIdleCallback === 'function') {
        const handle = w.requestIdleCallback(go, { timeout: IDLE_TIMEOUT_MS });
        return () => {
          alive = false;
          if (typeof w.cancelIdleCallback === 'function') w.cancelIdleCallback(handle);
        };
      }
      const timer = window.setTimeout(go, 0);
      return () => {
        alive = false;
        window.clearTimeout(timer);
      };
    }, [enabled, wanted, mod]);

    return mod;
  }

  return {
    useModule,
    load: start,
    resetForTests: () => {
      loaded = null;
      loading = null;
    },
  };
}
